import React from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

export interface LeftNavigationButtonsProps {
  currentTime: Date;
  onTimeChange: (hours: number) => void;
  isMobile: boolean;
  minTime: Date;
}

const LeftNavigationButtons: React.FC<LeftNavigationButtonsProps> = ({
  currentTime,
  onTimeChange,
  isMobile,
  minTime
}) => {
  // Media queries for responsive button visibility
  // Parent component (TimeNavigationControls) handles mobile vs desktop layout selection
  const isMobilePortrait = useMediaQuery('(orientation: portrait) and (max-width: 599px)');
  const isTabletPortrait = useMediaQuery('(orientation: portrait) and (min-width: 600px) and (max-width: 1023px)');

  // Check if we can go backward
  const canGoBack15Min = new Date(currentTime.getTime() - 15 * 60 * 1000) >= minTime;
  const canGoBack30Min = new Date(currentTime.getTime() - 30 * 60 * 1000) >= minTime;
  const canGoBack1Hr = new Date(currentTime.getTime() - 60 * 60 * 1000) >= minTime;
  const canGoBack4Hr = new Date(currentTime.getTime() - 4 * 60 * 60 * 1000) >= minTime;

  const buttonStyles = isMobile
    ? {
        minWidth: '70px',
        px: 1,
        py: 0.5,
        fontSize: '0.9rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1rem'
        }
      }
    : {
        minWidth: '90px',
        px: 1.2,
        py: 0.8,
        fontSize: '0.9rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1.1rem'
        }
      };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobilePortrait ? 1 : 1.5 }}>
      {/* Back 4 hours - always show */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(-4)}
        disabled={!canGoBack4Hr}
        variant="contained"
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoBack4Hr ? 1 : 0.5
        }}
        startIcon={
          <Box sx={{ display: 'flex', alignItems: 'center', ml: -0.5 }}>
            <KeyboardDoubleArrowLeftIcon sx={{ mr: -1.2 }} />
            <KeyboardArrowLeftIcon />
          </Box>
        }
      >
        -4h
      </Button>

      {/* Back 1 hour - hide on mobile portrait only */}
      {!isMobilePortrait && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(-1)}
          disabled={!canGoBack1Hr}
          variant="contained"
          startIcon={<KeyboardDoubleArrowLeftIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoBack1Hr ? 1 : 0.5
          }}
        >
          -1h
        </Button>
      )}

      {/* Back 30 minutes - hide on mobile portrait and tablet portrait */}
      {!isMobilePortrait && !isTabletPortrait && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(-0.5)}
          disabled={!canGoBack30Min}
          variant="contained"
          startIcon={<KeyboardArrowLeftIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoBack30Min ? 1 : 0.5
          }}
        >
          -30m
        </Button>
      )}

      {/* Back 15 minutes - always show */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(-0.25)}
        disabled={!canGoBack15Min}
        variant="contained"
        startIcon={<KeyboardArrowLeftIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoBack15Min ? 1 : 0.5
        }}
      >
        -15m
      </Button>
    </Box>
  );
};

export default LeftNavigationButtons;